import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Eyebrow,
  HeroPhoto,
  HeroWash,
  Rule,
  Wordmark,
} from "@/components/editorial";
import { PHOTOS } from "@/lib/photos";
import { env, getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import { useAuth } from "@/features/auth/AuthProvider";
import { runAfterAuth } from "@/features/auth/afterAuth";
import {
  isAppleSignInAvailable,
  isGoogleSignInAvailable,
} from "@/features/auth/nativeAuth";
import { authCallbackUrl } from "@/features/auth/deepLinks";

const schema = z.object({
  email: z.string().email("Email invalide"),
});
type FormValues = z.infer<typeof schema>;

const OTP_PATTERN = /^\d{6,10}$/;

/** `location.state.from` set by RequireAuth → "/path?query". */
function fromState(state: unknown): string | undefined {
  const from = (state as { from?: { pathname?: unknown; search?: unknown } })
    ?.from;
  if (!from || typeof from.pathname !== "string") return undefined;
  return from.pathname + (typeof from.search === "string" ? from.search : "");
}

export function LoginPage() {
  const configured = isSupabaseConfigured();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { session, signInWithGoogle, signInWithApple } = useAuth();
  const showGoogle = isGoogleSignInAvailable();
  const showApple = isAppleSignInAvailable();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthLoading, setOauthLoading] = useState<"google" | "apple" | null>(
    null,
  );
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Any flow that ends with a session while we are on /login (native
  // Google/Apple id-token, OTP code, or a user already signed in) goes
  // through the shared post-auth sync, then leaves the page (H2).
  const from = fromState(location.state);
  const userId = session?.user.id ?? null;
  useEffect(() => {
    if (!userId) return;
    let active = true;
    void runAfterAuth({ userId, queryClient, from }).then((result) => {
      if (!active) return;
      if (result.onboardingSyncFailed) {
        toast.error(t("auth.onboarding_sync_failed"));
      }
      navigate(result.destination, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [userId, from, navigate, queryClient, t]);

  async function handleGoogle() {
    setError(null);
    setOauthLoading("google");
    try {
      await signInWithGoogle();
    } catch {
      setError(t("auth.google_failed"));
    } finally {
      setOauthLoading(null);
    }
  }

  async function handleApple() {
    setError(null);
    setOauthLoading("apple");
    try {
      await signInWithApple();
    } catch {
      setError(t("auth.apple_failed"));
    } finally {
      setOauthLoading(null);
    }
  }

  async function onSubmit({ email }: FormValues) {
    setError(null);
    try {
      const { error: supabaseError } = await getSupabase().auth.signInWithOtp({
        email,
        options: {
          // Native: the app's custom URL scheme, so the link reopens the app.
          emailRedirectTo: authCallbackUrl(env.AUTH_REDIRECT_URL),
        },
      });
      if (supabaseError) throw supabaseError;
      setSentTo(email);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  }

  // The e-mail also carries a one-time code. Typing it in works where the
  // link cannot: inside the native app (no deep link / PKCE verifier in the
  // system browser) and when the mail is opened on another device (H4).
  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!sentTo) return;
    const token = otp.trim();
    if (!OTP_PATTERN.test(token)) {
      setOtpError(t("auth.otp_invalid_format"));
      return;
    }
    setOtpError(null);
    setVerifying(true);
    try {
      const { error: verifyError } = await getSupabase().auth.verifyOtp({
        email: sentTo,
        token,
        type: "email",
      });
      if (verifyError) throw verifyError;
      // Session propagates via onAuthStateChange → effect above navigates.
    } catch {
      setOtpError(t("auth.otp_failed"));
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="relative grid min-h-full grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      {/* Left: editorial photo panel */}
      <aside className="relative hidden overflow-hidden lg:block">
        <HeroPhoto src={PHOTOS.bowls} blur={3} opacity={0.95} />
        <HeroWash intensity="light" />
        <div className="relative flex h-full flex-col justify-between p-[72px]">
          <Wordmark size="md" />
          <div>
            <Eyebrow rule className="mb-7" accent>
              Numéro 24
            </Eyebrow>
            <h2 className="h-display max-w-[420px] text-[64px] leading-[0.95] text-foreground">
              Une après-midi.
              <br />
              <span className="italic text-accent">Sept</span> dîners.
            </h2>
            <p className="mt-6 max-w-[420px] font-serif text-xl font-light italic leading-snug text-ink-70">
              « Cuisiner moins, manger mieux. »
            </p>
          </div>
        </div>
      </aside>

      {/* Right: form */}
      <main className="flex min-h-full flex-col bg-background px-5 py-10 lg:px-20 lg:py-20">
        <div className="mb-12 flex items-center justify-between lg:hidden">
          <Wordmark size="md" />
        </div>

        <Link
          to="/"
          className="mb-12 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-ink-50 hover:text-foreground"
        >
          <ArrowLeft className="size-3" />
          Retour
        </Link>

        <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center">
          <Eyebrow className="mb-4">Connexion</Eyebrow>
          <h1 className="h-display mb-3 text-[56px] leading-[0.98]">
            Bon <span className="italic">retour</span>.
          </h1>
          <p className="mb-10 font-serif text-lg font-light leading-snug text-ink-70">
            Un lien magique vous attend dans votre boîte mail. Pas de mot de
            passe à retenir.
          </p>

          {!configured && (
            <div className="mb-6 border-l-2 border-accent bg-paper p-4 text-[13px] text-ink-70">
              Supabase n'est pas configuré. Renseignez{" "}
              <code className="font-mono text-foreground">
                VITE_SUPABASE_URL
              </code>{" "}
              et{" "}
              <code className="font-mono text-foreground">
                VITE_SUPABASE_ANON_KEY
              </code>{" "}
              dans <code className="font-mono">frontend/.env</code>.
            </div>
          )}

          {/* OAuth buttons */}
          <div className="space-y-3">
            {showGoogle && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                disabled={!configured || oauthLoading !== null}
                onClick={handleGoogle}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="size-4 shrink-0"
                  aria-hidden="true"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {oauthLoading === "google"
                  ? t("auth.oauth_loading")
                  : t("auth.google_cta")}
              </Button>
            )}

            {showApple && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                disabled={!configured || oauthLoading !== null}
                onClick={handleApple}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="size-4 shrink-0 fill-foreground"
                  aria-hidden="true"
                >
                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
                </svg>
                {oauthLoading === "apple"
                  ? t("auth.oauth_loading")
                  : t("auth.apple_cta")}
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="relative my-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-hair" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-ink-30">
              ou
            </span>
            <div className="h-px flex-1 bg-hair" />
          </div>

          {sentTo ? (
            <div className="border-t border-hair pt-8">
              <Eyebrow accent className="mb-3">
                {t("auth.magic_link_sent_title")}
              </Eyebrow>
              <p className="font-serif text-xl font-light leading-snug text-foreground">
                {t("auth.magic_link_sent_body")}
              </p>
              <form
                onSubmit={onVerifyOtp}
                className="mt-8 space-y-4"
                noValidate
              >
                <label
                  htmlFor="otp"
                  className="mb-2 block text-[10.5px] font-semibold uppercase tracking-[0.22em] text-ink-50"
                >
                  {t("auth.otp_label")}
                </label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={verifying}
                />
                {otpError && (
                  <p className="text-sm text-destructive" role="alert">
                    {otpError}
                  </p>
                )}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={verifying}
                >
                  {verifying ? t("auth.otp_submitting") : t("auth.otp_submit")}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setSentTo(null);
                    setOtp("");
                    setOtpError(null);
                  }}
                  className="text-[13px] text-ink-50 underline-offset-4 hover:underline"
                >
                  {t("auth.otp_resend")}
                </button>
              </form>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-8"
              noValidate
            >
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-[10.5px] font-semibold uppercase tracking-[0.22em] text-ink-50"
                >
                  Adresse mail
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="vous@exemple.fr"
                  disabled={!configured || isSubmitting}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="mt-2 text-xs text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={!configured || isSubmitting}
              >
                {isSubmitting ? "Envoi…" : "Recevoir le lien"}
                <ArrowRight />
              </Button>
            </form>
          )}

          <Rule className="my-10" />

          <p className="text-center text-[13px] text-ink-50">
            Pas encore de compte ?{" "}
            <Link
              to="/onboarding"
              className="font-serif italic text-accent underline-offset-4 hover:underline"
            >
              Commencer ici
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
